import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Home as HomeIcon, Building2, Castle, Banknote, Landmark, CreditCard, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import AvailabilityPicker, { slotLabel } from "@/components/AvailabilityPicker";
import { useSeo } from "@/lib/seo";
import { LocateMe } from "@/components/LocateMe";

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="text-sm text-slate-700">{label}</Label>{children}</div>;
const BANKS = ["BAC", "Banpro", "Lafise"];
const ICONS = [HomeIcon, Building2, Castle];
export const money = (p) => (p == null ? null : `$${Number(p).toFixed(0)}`);
export const nm = (x, lang, f = "name") => (lang === "es" && x?.[`${f}_es`]) || x?.[f] || "";
const pill = (on) => `px-4 py-2 rounded-full border text-sm transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 hover:border-blue-400"}`;

function SizeStep({ f, setF, cl, t, lang }) {
  const opts = [...(cl.packages || []).map((p, i) => [p.key, ICONS[i % 2], nm(p, lang), nm(p, lang, "desc"), money(p.price)]), ["larger", Castle, t("cl.bigger"), t("cl.bigger_d"), t("cl.quote")]];
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-3 gap-3">
        {opts.map(([k, Icon, title, desc, price]) => (
          <button key={k} type="button" onClick={() => setF({ ...f, size: k })} data-testid={`cl-size-${k}`}
            className={`text-start rounded-2xl border-2 p-5 transition-colors ${f.size === k ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}>
            <span className="h-11 w-11 rounded-xl flex items-center justify-center text-white bg-gradient-to-br from-sky-400 to-blue-600"><Icon className="h-5 w-5" /></span>
            <p className="mt-4 font-semibold text-slate-900">{title}</p>
            <p className="text-sm text-slate-500">{desc}</p>
            <p className="mt-3 font-mono text-2xl font-semibold text-blue-700" data-testid={`cl-price-${k}`}>{price}</p>
          </button>
        ))}
      </div>
      {f.size === "larger" && (
        <div className="grid grid-cols-2 gap-4">
          <F label={t("cl.beds")}><Input type="number" min={1} value={f.bedrooms} onChange={(e) => setF({ ...f, bedrooms: e.target.value })} data-testid="cl-bedrooms-input" /></F>
          <F label={t("cl.baths")}><Input type="number" min={1} value={f.bathrooms} onChange={(e) => setF({ ...f, bathrooms: e.target.value })} data-testid="cl-bathrooms-input" /></F>
        </div>
      )}
    </div>
  );
}

function ExtrasStep({ f, setF, cl, files, setFiles, t, lang }) {
  const toggle = (k) => setF({ ...f, addons: f.addons.includes(k) ? f.addons.filter((x) => x !== k) : [...f.addons, k] });
  return (
    <div className="space-y-6">
      <div>
        <p className="font-semibold mb-3">{t("cl.addons_t")}</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {(cl.addons || []).map((a) => (
            <label key={a.key} className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm cursor-pointer ${f.addons.includes(a.key) ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
              <span className="flex items-center gap-2"><Checkbox checked={f.addons.includes(a.key)} onCheckedChange={() => toggle(a.key)} data-testid={`cl-addon-${a.key}`} />{nm(a, lang)}</span>
              <span className="font-mono text-blue-700">+{money(a.price)}</span>
            </label>
          ))}
        </div>
      </div>
      <F label={t("cl.notes")}><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="cl-notes-input" /></F>
      <div>
        <p className="font-semibold">{t("cl.photos_t")}</p>
        <p className="text-sm text-slate-500 mb-2">{t("cl.photos_d")}</p>
        <Input type="file" multiple accept="image/*" onChange={(e) => setFiles([...e.target.files].slice(0, 8))} data-testid="cl-photos-input" />
        {files.length > 0 && <p className="text-xs text-slate-500 mt-1" data-testid="cl-photos-count">{files.length} ✓</p>}
      </div>
    </div>
  );
}

function ContactStep({ f, setF, acct, setAcct, user, cfg, t }) {
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <LocateMe geo={f.geo} onFound={({ geo, city, neighborhood }) => setF((p) => ({ ...p, geo, city: p.city || city, neighborhood: p.neighborhood || neighborhood }))} />
      <F label={`${t("f.city")} *`}><Input list="cl-area-list" value={f.city} onChange={set("city")} data-testid="cl-city-input" /></F>
      <datalist id="cl-area-list">{(cfg.areas || []).map((a) => <option key={a.name} value={a.name} />)}</datalist>
      <F label={t("req.neighborhood")}><Input value={f.neighborhood} onChange={set("neighborhood")} data-testid="cl-neighborhood-input" /></F>
      <div className="sm:col-span-2"><F label={`${t("req.directions")} *`}><Textarea rows={2} value={f.directions} onChange={set("directions")} data-testid="cl-directions-input" /></F></div>
      <div className="sm:col-span-2"><F label={t("req.access")}><Input value={f.access} onChange={set("access")} data-testid="cl-access-input" /></F></div>
      <F label={`${t("f.name")} *`}><Input value={f.contact_name} onChange={set("contact_name")} data-testid="cl-name-input" /></F>
      <F label={`${t("f.phone")} *`}><Input value={f.contact_phone} onChange={set("contact_phone")} data-testid="cl-phone-input" /></F>
      <div className="sm:col-span-2 flex gap-2 flex-wrap">
        {["whatsapp", "phone", "email"].map((m) => <button type="button" key={m} onClick={() => setF({ ...f, contact_method: m })} className={pill(f.contact_method === m)} data-testid={`cl-method-${m}`}>{t(`m.${m}`)}</button>)}
      </div>
      {!user && (
        <div className="sm:col-span-2 rounded-2xl bg-slate-50 border border-slate-200 p-4 grid sm:grid-cols-2 gap-4">
          <p className="sm:col-span-2 font-semibold text-sm">{t("req.account")}</p>
          <F label={`${t("f.email")} *`}><Input type="email" value={acct.email} onChange={(e) => setAcct({ ...acct, email: e.target.value })} data-testid="cl-account-email-input" /></F>
          <F label={`${t("f.password")} *`}><Input type="password" value={acct.password} onChange={(e) => setAcct({ ...acct, password: e.target.value })} data-testid="cl-account-password-input" /></F>
          <Link to="/login?next=/cleaning" className="sm:col-span-2 text-sm text-blue-700 hover:underline" data-testid="cl-login-link">{t("req.have_account")}</Link>
        </div>
      )}
    </div>
  );
}

function PaymentPick({ f, setF, t }) {
  return (
    <div className="space-y-3" data-testid="cl-payment">
      <p className="font-semibold text-sm">{t("cl.pay")}</p>
      <div className="grid sm:grid-cols-3 gap-2">
        <button type="button" onClick={() => setF({ ...f, payment_method: "cash" })} className={`${pill(f.payment_method === "cash")} flex items-center gap-2 justify-center`} data-testid="cl-pay-cash"><Banknote className="h-4 w-4" />{t("cl.cash")}</button>
        <button type="button" onClick={() => setF({ ...f, payment_method: "bank" })} className={`${pill(f.payment_method === "bank")} flex items-center gap-2 justify-center`} data-testid="cl-pay-bank"><Landmark className="h-4 w-4" />{t("cl.bank")}</button>
        <span className="px-4 py-2 rounded-full border border-dashed border-slate-300 text-sm text-slate-400 flex items-center gap-2 justify-center" data-testid="cl-pay-card-soon"><CreditCard className="h-4 w-4" />{t("cl.card_soon")}</span>
      </div>
      {f.payment_method === "bank" && (
        <div className="flex gap-2 flex-wrap items-center"><span className="text-sm text-slate-600">{t("cl.choose_bank")}:</span>
          {BANKS.map((b) => <button type="button" key={b} onClick={() => setF({ ...f, bank: b })} className={pill(f.bank === b)} data-testid={`cl-bank-${b}`}>{b}</button>)}
        </div>
      )}
    </div>
  );
}

export function BankDetails({ bank, banks, number, t }) {
  const info = (banks || {})[bank];
  return (
    <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4 text-sm text-start" data-testid="cl-bank-details">
      <p className="font-semibold text-blue-900">{t("cl.bank_info")} · {bank}</p>
      <p className="mt-1 whitespace-pre-wrap text-slate-700">{info || t("cl.bank_pending")}</p>
      <p className="mt-1 text-xs text-slate-500">{t("cl.bank_ref", { n: number })}</p>
    </div>
  );
}

export function ReceiptUpload({ rid, done, onDone, t }) {
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(done);
  const up = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("stage", "receipt");
    try { await api.post(`/requests/${rid}/files`, fd); setOk(true); toast.success(t("cl.receipt_ok")); onDone?.(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-dashed border-blue-300 p-4 text-sm text-start" data-testid="cl-receipt-upload">
      <p className="font-semibold">{t("cl.receipt_t")}</p>
      {ok && <p className="text-emerald-700 mt-1" data-testid="cl-receipt-done">{t("cl.receipt_done")} ✓</p>}
      <label className="mt-2 inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 cursor-pointer transition-colors">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{t("cl.receipt_btn")}
        <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => up(e.target.files[0])} data-testid="cl-receipt-input" />
      </label>
    </div>
  );
}

function Done({ res, f, t }) {
  const nav = useNavigate();
  const quote = f.size === "larger";
  return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="cl-confirmation">
      <CheckCircle2 className="h-14 w-14 text-blue-600 mx-auto" />
      <h1 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight">{t(quote ? "cl.done_quote" : "cl.done")}</h1>
      <p className="mt-3 text-slate-600">{t(quote ? "cl.done_quote_d" : "cl.done_d")}</p>
      <p className="mt-4 font-mono text-xl font-semibold" data-testid="cl-confirmation-number">{res.number}</p>
      {f.payment_method === "bank" && <div className="mt-6 space-y-3"><BankDetails bank={f.bank} banks={res.banks} number={res.number} t={t} /><ReceiptUpload rid={res.request.id} t={t} /></div>}
      <Button className="mt-8 rounded-full bg-blue-600 hover:bg-blue-700" onClick={() => nav(`/dashboard/requests/${res.request.id}`)} data-testid="cl-open-booking-btn">{t("conf.view")}</Button>
    </div>
  );
}

const STEPS = ["cl.s1", "cl.s_extras", "cl.s2", "cl.s3", "cl.s4"];

export default function BookCleaning() {
  const { t, lang } = useI18n();
  const { user, signIn } = useAuth();
  useSeo(t("cl.title"), t("cl.sub"));
  const [step, setStep] = useState(1);
  const [cfg, setCfg] = useState({ areas: [], cleaning: { packages: [], addons: [] } });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [files, setFiles] = useState([]);
  const [acct, setAcct] = useState({ email: "", password: "" });
  const [f, setF] = useState({ size: "", bedrooms: 4, bathrooms: 3, notes: "", addons: [], slots: [], geo: null, city: "", neighborhood: "", directions: "", access: "",
    contact_name: "", contact_phone: "", contact_method: "whatsapp", payment_method: "cash", bank: "", consent: false });
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)); }, []);
  useEffect(() => {
    if (user) setF((p) => ({ ...p, contact_name: p.contact_name || user.name, contact_phone: p.contact_phone || user.phone || "" }));
  }, [user]);
  const cl = cfg.cleaning || {};
  const pkg = (cl.packages || []).find((p) => p.key === f.size);
  const chosen = (cl.addons || []).filter((a) => f.addons.includes(a.key));
  const total = pkg ? pkg.price + chosen.reduce((s, a) => s + a.price, 0) : null;
  const slotText = f.slots.map((s) => slotLabel(s, lang, t)).join("; ");
  const sizeLabel = f.size === "larger" ? `${t("cl.bigger")} · ${f.bedrooms} / ${f.bathrooms}` : nm(pkg, lang);

  const valid = () => {
    if (step === 1) return !!f.size && (f.size !== "larger" || (f.bedrooms > 0 && f.bathrooms > 0));
    if (step === 3) return f.slots.length > 0;
    if (step === 4) return f.city.trim() && f.directions.trim() && f.contact_name && f.contact_phone && (user || (acct.email && acct.password.length >= 6));
    if (step === 5) return f.consent && (f.payment_method !== "bank" || f.bank);
    return true;
  };
  const next = () => { if (valid()) { setStep(step + 1); window.scrollTo(0, 0); } else toast.error(t("req.required")); };
  const submit = async () => {
    if (!valid()) { toast.error(t("req.required")); return; }
    setBusy(true);
    try {
      if (!user) {
        const r = await api.post("/auth/register", { name: f.contact_name, email: acct.email, password: acct.password, phone: f.contact_phone });
        signIn(r.data);
      }
      const { slots, ...rest } = f;
      const r = await api.post("/cleanings", { ...rest, bedrooms: Number(f.bedrooms) || 0, bathrooms: Number(f.bathrooms) || 0, slot_text: slotText });
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("stage", "request");
        await api.post(`/requests/${r.data.request.id}/files`, fd).catch((e) => toast.error(errMsg(e)));
      }
      setRes(r.data);
      window.scrollTo(0, 0);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (res) return <Done res={res} f={f} t={t} />;
  if (user && user.role !== "customer") return <p className="max-w-2xl mx-auto px-4 py-24 text-center text-slate-600" data-testid="cl-only-customers">{t("cl.only_customers")}</p>;

  const review = (
    <div className="space-y-5" data-testid="cl-review">
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm rounded-2xl bg-slate-50 p-5">
        {[["cl.s1", sizeLabel], ["cl.extras", chosen.map((a) => `${nm(a, lang)} +${money(a.price)}`).join(", ") || "—"], ["cl.total", total != null ? money(total) : t("cl.quote")],
          ["req.times", slotText], ["f.city", [f.city, f.neighborhood].filter(Boolean).join(", ")], ["req.directions", f.directions], ["f.name", f.contact_name],
          ["f.phone", `${f.contact_phone} · ${t(`m.${f.contact_method}`)}`]].map(([k, v]) => (
          <div key={k}><dt className="text-slate-500 text-xs">{t(k)}</dt><dd className="font-medium text-slate-900 whitespace-pre-wrap" data-testid={`cl-review-${k.replace(".", "-")}`}>{v}</dd></div>
        ))}
      </dl>
      <PaymentPick f={f} setF={setF} t={t} />
      <label className="flex items-start gap-2 text-sm"><Checkbox checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })} data-testid="cl-consent-checkbox" className="mt-0.5" />{t("req.consent")}</label>
    </div>
  );
  const body = {
    1: <SizeStep f={f} setF={setF} cl={cl} t={t} lang={lang} />,
    2: <ExtrasStep f={f} setF={setF} cl={cl} files={files} setFiles={setFiles} t={t} lang={lang} />,
    3: <div className="space-y-3"><p className="text-sm text-slate-600">{t("cl.when")}</p><AvailabilityPicker slots={f.slots} onChange={(slots) => setF({ ...f, slots })} /></div>,
    4: <ContactStep f={f} setF={setF} acct={acct} setAcct={setAcct} user={user} cfg={cfg} t={t} />,
    5: review,
  };
  return (
    <div className="max-w-3xl mx-auto px-4 pt-12 pb-28" data-testid="cleaning-page">
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">{t("cl.title")}</h1>
      <p className="mt-3 text-slate-600">{t("cl.sub")}</p>
      <div className="mt-6 flex items-center justify-between text-sm">
        <span className="font-semibold text-blue-700" data-testid="cl-step-label">{t(STEPS[step - 1])}</span>
        <span className="flex items-center gap-3">
          {total != null && <span className="font-mono font-semibold text-blue-700" data-testid="cl-running-total">{money(total)}</span>}
          <span className="font-mono text-slate-500">{t("req.step", { n: step, total: STEPS.length })}</span>
        </span>
      </div>
      <Progress value={(step / STEPS.length) * 100} className="mt-2 h-2" />
      <div className="mt-8 rounded-3xl bg-white border border-slate-200 p-6 md:p-8 fade-up" key={step}>{body[step]}</div>
      <div className="mt-6 flex justify-between">
        <Button variant="outline" className="rounded-full" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="cl-back-btn">{t("req.back")}</Button>
        {step < STEPS.length ? (
          <Button className="rounded-full bg-blue-600 hover:bg-blue-700 px-8" onClick={next} data-testid="cl-next-btn">{t("req.next")}</Button>
        ) : (
          <Button className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8" disabled={busy} onClick={submit} data-testid="cl-submit-btn">
            {busy && <Loader2 className="h-4 w-4 me-2 animate-spin" />}{t(f.size === "larger" ? "cl.submit_quote" : "cl.submit")}
          </Button>
        )}
      </div>
    </div>
  );
}
